import React from 'react';
import { type VariantProps } from 'class-variance-authority';
import { Loader2, PlusIcon, FilterIcon, PencilIcon, UploadIcon, ChevronDownIcon, ChevronUpIcon, ArrowRightIcon, Trash2Icon } from 'lucide-react';
import { Button, buttonVariants } from 'shadcn-ui';
import { ButtonColor, ButtonSize, FormButtonLayoutProps, IconType } from 'ui-builder';

type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;
type ButtonSizeVariant = NonNullable<VariantProps<typeof buttonVariants>['size']>;

function mapColorToVariant(color?: ButtonColor): ButtonVariant {
  switch (color) {
    case ButtonColor.GREEN:
      return 'default';
    case ButtonColor.BLUE:
    case ButtonColor.DARK_BLUE:
      return 'default';
    case ButtonColor.RED:
      return 'destructive';
    case ButtonColor.GRAY:
      return 'secondary';
    case ButtonColor.BLUE_LIGHT:
      return 'outline';
    case ButtonColor.WHITE:
      return 'ghost';
    case ButtonColor.YELLOW:
      return 'default';
    default:
      return 'default';
  }
}

function mapSize(size?: ButtonSize): ButtonSizeVariant {
  switch (size) {
    case ButtonSize.SMALL:
      return 'sm';
    case ButtonSize.MEDIUM:
      return 'default';
    case ButtonSize.BIG:
      return 'default';
    default:
      return 'default';
  }
}

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  [IconType.PLUS]: PlusIcon,
  [IconType.FILTER]: FilterIcon,
  [IconType.PENCIL]: PencilIcon,
  [IconType.UPLOAD]: UploadIcon,
  [IconType.ARROW_DOWN]: ChevronDownIcon,
  [IconType.ARROW_UP]: ChevronUpIcon,
  [IconType.ARROW_RIGHT]: ArrowRightIcon,
  [IconType.TRASH]: Trash2Icon,
};

function ShadcnSubmitButtonLayout(props: FormButtonLayoutProps) {
  const variant = mapColorToVariant(props.color);
  const size = mapSize(props.size);
  const IconComponent = props.iconType ? ICON_MAP[props.iconType] : undefined;

  return (
    <Button
      type="submit"
      variant={variant}
      size={size}
      disabled={props.isDisabled || props.isLoading}
      onClick={(e) => {
        e.preventDefault();
        props.onClick();
      }}
      data-testid={props.testId}
    >
      {props.isLoading && <Loader2 className="animate-spin" />}
      {!props.isLoading && IconComponent && <IconComponent className="size-4" />}
      {props.text}
    </Button>
  );
}

export default ShadcnSubmitButtonLayout;
